import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { PaymentScheduleStage } from './PaymentScheduleStage';
import {
  PaymentScheduleFormValues,
  type ProposalTemplateStageCardProps,
} from './types';
import { useStagePercentageField } from './useStagePercentageField';

const ROW_CLASS_NAME = 'flex flex-col gap-4 @2xl:flex-row';
const DESCRIPTION_CLASS_NAME = '@2xl:flex-1';
// Room for two number fields, so the description is one width for every stage.
const NUMBERS_CLASS_NAME = 'flex flex-wrap gap-4 @2xl:w-84 @2xl:shrink-0';

export const ProposalTemplateInstalmentStageCard: FC<
  ProposalTemplateStageCardProps
> = (props) => {
  const { index, remainingPercentage, onDepositChange, onRemove } = props;

  const percentage = useStagePercentageField(index, 'instalment');

  return (
    <PaymentScheduleStage.CardShell
      index={index}
      isInvalid={remainingPercentage < 0}
      onRemove={onRemove}
      actions={
        <>
          {onDepositChange && (
            <PaymentScheduleStage.DepositCheckbox
              index={index}
              isDeposit={false}
              onChange={onDepositChange}
            />
          )}
          <PaymentScheduleStage.AdjustPercentageButton
            percentage={percentage}
            remainingPercentage={remainingPercentage}
          />
        </>
      }
    >
      <div className={ROW_CLASS_NAME}>
        <div className={DESCRIPTION_CLASS_NAME}>
          <PaymentScheduleStage.DescriptionField index={index} />
        </div>
        <div className={NUMBERS_CLASS_NAME}>
          <PaymentScheduleStage.PercentageField percentage={percentage} />
        </div>
      </div>
    </PaymentScheduleStage.CardShell>
  );
};

export const ProposalTemplateDepositStageCard: FC<
  ProposalTemplateStageCardProps
> = (props) => {
  const { index } = props;
  const { control } = useFormContext<PaymentScheduleFormValues>();
  const basisType = useWatch({
    control,
    name: `paymentSchedule.stages.${index}.basis.type`,
  });

  return basisType === 'fixed' ? (
    <FixedDepositStageCard {...props} />
  ) : (
    <PercentageDepositStageCard {...props} />
  );
};

const PercentageDepositStageCard: FC<ProposalTemplateStageCardProps> = (
  props
) => {
  const { index, remainingPercentage, onDepositChange, onRemove } = props;
  const isFixedDepositEnabled = useMyAccountHasFeature('hasFixedDeposits');

  const percentage = useStagePercentageField(index, 'deposit');

  return (
    <PaymentScheduleStage.CardShell
      index={index}
      isInvalid={remainingPercentage < 0}
      onRemove={onRemove}
      actions={
        <>
          {onDepositChange && (
            <PaymentScheduleStage.DepositCheckbox
              index={index}
              isDeposit
              onChange={onDepositChange}
            />
          )}
          <PaymentScheduleStage.AdjustPercentageButton
            percentage={percentage}
            remainingPercentage={remainingPercentage}
          />
        </>
      }
    >
      {isFixedDepositEnabled && (
        <PaymentScheduleStage.DepositTypeField index={index} />
      )}
      <div className={ROW_CLASS_NAME}>
        <div className={DESCRIPTION_CLASS_NAME}>
          <PaymentScheduleStage.DescriptionField index={index} />
        </div>
        <div className={NUMBERS_CLASS_NAME}>
          <PaymentScheduleStage.PercentageField percentage={percentage} />
        </div>
      </div>
    </PaymentScheduleStage.CardShell>
  );
};

const FixedDepositStageCard: FC<ProposalTemplateStageCardProps> = (props) => {
  const { index, remainingPercentage, onDepositChange, onRemove } = props;
  const isFixedDepositEnabled = useMyAccountHasFeature('hasFixedDeposits');

  return (
    <PaymentScheduleStage.CardShell
      index={index}
      isInvalid={remainingPercentage < 0}
      onRemove={onRemove}
      actions={
        onDepositChange && (
          <PaymentScheduleStage.DepositCheckbox
            index={index}
            isDeposit
            onChange={onDepositChange}
          />
        )
      }
    >
      {isFixedDepositEnabled && (
        <PaymentScheduleStage.DepositTypeField index={index} />
      )}
      <div className={ROW_CLASS_NAME}>
        <div className={DESCRIPTION_CLASS_NAME}>
          <PaymentScheduleStage.DescriptionField index={index} />
        </div>
        <div className={NUMBERS_CLASS_NAME}>
          <PaymentScheduleStage.FixedDepositFields index={index} />
        </div>
      </div>
    </PaymentScheduleStage.CardShell>
  );
};
